import { useEffect, useState } from 'react';


/** localStorage key for persisting timezone preference */
const LS_KEY = 'useLocalZone';

/**
 * A React hook that manages user preference for displaying time in local timezone vs UTC.
 * The preference is persisted in localStorage and handles SSR hydration.
 *
 * @returns A tuple containing:
 *   - boolean: Current timezone preference (true for local, false for UTC)
 *   - function: Setter function to update the timezone preference
 *   - boolean: Hydration status indicating if the client-side code has loaded.
 *        (Use this if you want to avoid "flickering" when the client-side code loads.)
 */
export function useLocalZonePreference(): [boolean, ( next: boolean ) => void, boolean] {
  // Stable SSR value: true (default to local timezone)
  const [ useLocalZone, setUseLocalZone ] = useState<boolean>( true );
  const [ hydrated, setHydrated ] = useState( false );

  useEffect( () => {
    setHydrated( true );
    try {
      const stored = JSON.parse( localStorage.getItem( LS_KEY ) ?? 'true' );
      setUseLocalZone( !!stored );
    } catch {
      // keep default true
    }
  }, [] );

  useEffect( () => {
    if ( !hydrated ) return;
    try {
      localStorage.setItem( LS_KEY, JSON.stringify( useLocalZone ) );
    } catch { /* empty */ }
  }, [ useLocalZone, hydrated ] );

  return [ useLocalZone, ( next: boolean ) => setUseLocalZone( next ), hydrated ];
}
